import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronDown, ChevronRight, GraduationCap, Shield, TrendingUp } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { applyRateCardEscalation } from '../../../lib/utils/pricingEngine';
import { useTenantId } from '../../../pages/Delivery/_shared/hooks/useTenantId';
import { useRateSelections } from '../../../pages/Delivery/Registries/hooks';
import { ParameterCell } from './ParameterCell';
import { PinnedSelection, RateEvidencePanel } from './RateEvidencePanel';
const SOURCE_LABELS = {
    extracted: {
        label: 'Extracted',
        color: 'bg-green-50 text-green-700 border-green-200'
    },
    vehicle_catalog: {
        label: 'Vehicle',
        color: 'bg-purple-50 text-purple-700 border-purple-200'
    },
    manual: {
        label: 'Manual',
        color: 'bg-gray-50 text-gray-600 border-gray-200'
    },
    xlsx_lcat_sheet: {
        label: 'XLSX',
        color: 'bg-blue-50 text-blue-700 border-blue-200'
    }
};
function QualificationsTooltip({ cat }) {
    const q = cat.qualifications;
    if (!q) return null;
    const parts = [];
    if (q.education) parts.push(q.education);
    if (q.yearsExperience) parts.push(`${q.yearsExperience}+ years`);
    if (q.clearanceRequired) parts.push(q.clearanceRequired);
    if (q.certifications?.length) parts.push(q.certifications.join(', '));
    if (parts.length === 0) return null;
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex items-center gap-1 text-gray-400",
        title: parts.join(' | '),
        children: [
            /*#__PURE__*/ _jsx(GraduationCap, {
                className: "w-3.5 h-3.5"
            }),
            q.clearanceRequired && /*#__PURE__*/ _jsx(Shield, {
                className: "w-3.5 h-3.5 text-amber-400"
            })
        ]
    });
}
const LOCATION_LABELS = {
    on_site: 'On-Site',
    off_site: 'Off-Site'
};
/**
 * Parse rate keys into structured columns.
 * Keys may be "default", "Base Period", or "Base Period.on_site" (period.location).
 */ function parseRateColumns(sortedKeys, hasLocations) {
    if (!hasLocations) {
        // No location variants — flat columns
        return {
            groups: [
                {
                    label: '',
                    keys: sortedKeys.map((k)=>({
                            key: k,
                            subLabel: k === 'default' ? 'Rate' : k
                        }))
                }
            ],
            flat: true
        };
    }
    // Parse "period.location" keys and group by period
    const periodMap = new Map();
    for (const key of sortedKeys){
        const dotIdx = key.lastIndexOf('.');
        if (dotIdx > 0) {
            const period = key.substring(0, dotIdx);
            const location = key.substring(dotIdx + 1);
            if (location === 'on_site' || location === 'off_site') {
                const existing = periodMap.get(period) ?? [];
                existing.push({
                    key,
                    location
                });
                periodMap.set(period, existing);
                continue;
            }
        }
        // Non-composite key
        const existing = periodMap.get(key) ?? [];
        existing.push({
            key,
            location: ''
        });
        periodMap.set(key, existing);
    }
    const groups = [
        ...periodMap.entries()
    ].map(([period, entries])=>({
            label: period === 'default' ? 'Rate' : period,
            keys: entries.map((e)=>({
                    key: e.key,
                    subLabel: e.location ? LOCATION_LABELS[e.location] || e.location : ''
                }))
        }));
    return {
        groups,
        flat: false
    };
}
// ── Escalation Panel ────────────────────────────────────────────────
function EscalationPanel({ rateCard, periods, onChange }) {
    const [showPerPeriod, setShowPerPeriod] = useState(false);
    const escalation = rateCard.escalation;
    const defaultPct = escalation?.defaultPct ?? 0;
    const handleDefaultChange = useCallback((pct)=>{
        onChange({
            ...rateCard,
            escalation: {
                ...rateCard.escalation,
                defaultPct: pct,
                perPeriodOverrides: rateCard.escalation?.perPeriodOverrides
            }
        });
    }, [
        rateCard,
        onChange
    ]);
    const handlePerPeriodChange = useCallback((period, pct)=>{
        onChange({
            ...rateCard,
            escalation: {
                defaultPct: rateCard.escalation?.defaultPct ?? 0,
                perPeriodOverrides: {
                    ...rateCard.escalation?.perPeriodOverrides,
                    [period]: pct
                }
            }
        });
    }, [
        rateCard,
        onChange
    ]);
    const handleApply = useCallback(()=>{
        if (periods.length < 2) return;
        const basePeriod = periods[0];
        const updated = applyRateCardEscalation(rateCard, basePeriod, escalation?.defaultPct ?? 0, escalation?.perPeriodOverrides);
        // Preserve escalation config on the result
        onChange({
            ...updated,
            escalation: rateCard.escalation
        });
    }, [
        rateCard,
        periods,
        escalation,
        onChange
    ]);
    if (periods.length < 2) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(TrendingUp, {
                                className: "w-4 h-4 text-amber-600"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-sm font-medium text-amber-800",
                                children: "Rate Escalation"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: handleApply,
                        className: "text-xs px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700",
                        children: "Apply to Rates"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs text-amber-700",
                        children: "Default %"
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        type: "number",
                        value: (defaultPct * 100).toFixed(1),
                        onChange: (e)=>handleDefaultChange(Number(e.target.value) / 100),
                        className: "w-20 px-2 py-1 text-xs border border-amber-300 rounded text-right focus:outline-none focus:ring-1 focus:ring-amber-500",
                        step: "0.1"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-amber-600",
                        children: "per year"
                    }),
                    /*#__PURE__*/ _jsxs("button", {
                        onClick: ()=>setShowPerPeriod(!showPerPeriod),
                        className: "ml-auto text-xs text-amber-600 hover:text-amber-700 flex items-center gap-0.5",
                        children: [
                            showPerPeriod ? /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "w-3 h-3"
                            }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                className: "w-3 h-3"
                            }),
                            "Per-period"
                        ]
                    })
                ]
            }),
            showPerPeriod && /*#__PURE__*/ _jsx("div", {
                className: "space-y-1 pl-4",
                children: periods.slice(1).map((period)=>{
                    const override = escalation?.perPeriodOverrides?.[period];
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs text-amber-700 w-28 truncate",
                                children: period
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                type: "number",
                                value: override != null ? (override * 100).toFixed(1) : (defaultPct * 100).toFixed(1),
                                onChange: (e)=>handlePerPeriodChange(period, Number(e.target.value) / 100),
                                className: `w-20 px-2 py-0.5 text-xs border rounded text-right focus:outline-none focus:ring-1 focus:ring-amber-500 ${override != null ? 'border-amber-400 bg-white' : 'border-gray-200 bg-gray-50'}`,
                                step: "0.1"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-[10px] text-amber-500",
                                children: "%"
                            })
                        ]
                    }, period);
                })
            })
        ]
    });
}
export const RateCardEditor = /*#__PURE__*/ memo(function RateCardEditor({ rateCard, projectId, scenarioId, onChange }) {
    const { categories } = rateCard;
    const [evidenceFor, setEvidenceFor] = useState(null);
    // Rate evidence is registry content: without registry authority the consumer
    // shows no evidence controls and asks for nothing.
    const companyId = useTenantId();
    const canSeeEvidence = useTenantAreas().registries;
    const selections = useRateSelections(projectId, companyId, scenarioId, canSeeEvidence);
    const selectionByRef = new Map((selections.data ?? []).map((s)=>[
            s.consumerRef,
            s
        ]));
    const hasLocations = (rateCard.locationVariants?.length ?? 0) > 0;
    // Collect all unique rate keys across categories
    const sortedKeys = useMemo(()=>{
        const rateKeys = new Set();
        for (const cat of categories){
            for (const key of Object.keys(cat.rates)){
                rateKeys.add(key);
            }
        }
        return [
            ...rateKeys
        ].sort();
    }, [
        categories
    ]);
    // Extract period names (strip location suffixes)
    const periods = useMemo(()=>{
        const periodSet = new Set();
        for (const key of sortedKeys){
            const dotIdx = key.lastIndexOf('.');
            if (dotIdx > 0 && (key.endsWith('.on_site') || key.endsWith('.off_site'))) {
                periodSet.add(key.substring(0, dotIdx));
            } else {
                periodSet.add(key);
            }
        }
        return [
            ...periodSet
        ].sort();
    }, [
        sortedKeys
    ]);
    const { groups: columnGroups, flat } = useMemo(()=>parseRateColumns(sortedKeys, hasLocations), [
        sortedKeys,
        hasLocations
    ]);
    // Flat list of all rate keys in display order
    const allRateKeys = useMemo(()=>columnGroups.flatMap((g)=>g.keys.map((k)=>k.key)), [
        columnGroups
    ]);
    const handleRateChange = useCallback((catIndex, rateKey, value)=>{
        const updatedCategories = categories.map((cat, i)=>{
            if (i !== catIndex) return cat;
            const existingParam = cat.rates[rateKey];
            if (!existingParam) return cat;
            const updatedParam = {
                ...existingParam,
                value
            };
            return {
                ...cat,
                rates: {
                    ...cat.rates,
                    [rateKey]: updatedParam
                }
            };
        });
        onChange({
            ...rateCard,
            categories: updatedCategories
        });
    }, [
        categories,
        rateCard,
        onChange
    ]);
    // Selecting is the deliberate act that sets the working input; the first rate
    // column takes it. The Rate Book itself is never written.
    const applySelected = useCallback((catIndex, amount)=>handleRateChange(catIndex, allRateKeys[0], amount), [
        handleRateChange,
        allRateKeys
    ]);
    if (categories.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center py-12 text-gray-400 text-sm",
            children: "No labor categories in rate card"
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(EscalationPanel, {
                rateCard: rateCard,
                periods: periods,
                onChange: onChange
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full border-collapse",
                    children: [
                        /*#__PURE__*/ _jsxs("thead", {
                            children: [
                                !flat && /*#__PURE__*/ _jsxs("tr", {
                                    className: "border-b border-gray-100",
                                    children: [
                                        /*#__PURE__*/ _jsx("th", {
                                            colSpan: 2
                                        }),
                                        columnGroups.map((group)=>/*#__PURE__*/ _jsx("th", {
                                                colSpan: group.keys.length,
                                                className: "px-3 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-100",
                                                children: group.label
                                            }, group.label)),
                                        /*#__PURE__*/ _jsx("th", {
                                            colSpan: 2
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    className: "border-b-2 border-gray-200",
                                    children: [
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                            children: "Labor Category"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16",
                                            children: "Level"
                                        }),
                                        flat ? columnGroups[0]?.keys.map((k)=>/*#__PURE__*/ _jsx("th", {
                                                className: "px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                children: k.subLabel
                                            }, k.key)) : columnGroups.flatMap((group)=>group.keys.map((k)=>/*#__PURE__*/ _jsx("th", {
                                                    className: "px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider",
                                                    children: k.subLabel || group.label
                                                }, k.key))),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20",
                                            children: "Source"
                                        }),
                                        /*#__PURE__*/ _jsx("th", {
                                            className: "px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12",
                                            children: "Quals"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: categories.map((cat, idx)=>{
                                const sourceInfo = SOURCE_LABELS[cat.source] || SOURCE_LABELS.manual;
                                const pinned = canSeeEvidence ? selectionByRef.get(cat.id) : undefined;
                                return /*#__PURE__*/ _jsxs("tr", {
                                    className: "border-b border-gray-100 hover:bg-gray-50/50",
                                    children: [
                                        /*#__PURE__*/ _jsxs("td", {
                                            className: "px-3 py-2 text-sm font-medium text-gray-900",
                                            children: [
                                                cat.title,
                                                canSeeEvidence && /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "ghost",
                                                    "data-testid": `${Testids.rateSelection.evidenceOpen}-${cat.id}`,
                                                    onClick: ()=>setEvidenceFor(evidenceFor === cat.id ? null : cat.id),
                                                    children: "Rate evidence"
                                                }),
                                                pinned && /*#__PURE__*/ _jsx(PinnedSelection, {
                                                    projectId: projectId,
                                                    companyId: companyId,
                                                    scenarioId: scenarioId,
                                                    category: cat,
                                                    selection: pinned,
                                                    onApply: (amount)=>applySelected(idx, amount)
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2 text-sm text-gray-500",
                                            children: cat.level || '—'
                                        }),
                                        allRateKeys.map((key)=>{
                                            const param = cat.rates[key];
                                            if (!param) {
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: "px-1 py-1 text-sm text-gray-400 text-right",
                                                    children: "—"
                                                }, key);
                                            }
                                            return /*#__PURE__*/ _jsx("td", {
                                                className: "px-1 py-1",
                                                "data-testid": `${Testids.pricingRateCard.rateCell}-${cat.laborCategoryId}-${key}`,
                                                children: /*#__PURE__*/ _jsx(ParameterCell, {
                                                    param: param,
                                                    editable: param.source !== 'government',
                                                    onChange: (val)=>handleRateChange(idx, key, val)
                                                })
                                            }, key);
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2 text-center",
                                            children: /*#__PURE__*/ _jsx(Badge, {
                                                variant: "outline",
                                                className: `text-[10px] px-1.5 py-0 ${sourceInfo.color}`,
                                                children: sourceInfo.label
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-3 py-2 text-center",
                                            children: /*#__PURE__*/ _jsx(QualificationsTooltip, {
                                                cat: cat
                                            })
                                        })
                                    ]
                                }, cat.id);
                            })
                        })
                    ]
                })
            }),
            categories.map((cat, idx)=>canSeeEvidence && evidenceFor === cat.id ? /*#__PURE__*/ _jsx(RateEvidencePanel, {
                    projectId: projectId,
                    companyId: companyId,
                    scenarioId: scenarioId,
                    category: cat,
                    onApply: (amount)=>applySelected(idx, amount)
                }, cat.id) : null)
        ]
    });
});
